import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';
import { resolveFlowControlExpandIdFromSegment } from '@/helpers/flow-control-playing-segment.helper';
import {
  FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID,
  FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID,
} from '@/components/flows/detail/slot-list/flow-slot-assets-inline-ids';

export function isFlowControlSlotBrowsable(slot: FlowSlotDto): boolean {
  return slot.contentType === 'Session' ? Boolean(slot.content?.title) : Boolean(slot.title);
}

function segmentKey(row: SimulationCueSheetRow): string {
  return `${row.segmentKind}|${row.segmentAnchor}|${row.slotId ?? ''}`;
}

function resolveExpandIdFromCueRow(row: SimulationCueSheetRow): string | null {
  const slotId =
    row.slotId ?? (row.segmentAnchor.startsWith('slot:') ? row.segmentAnchor.slice('slot:'.length) : undefined);
  return resolveFlowControlExpandIdFromSegment(row.segmentKind, slotId, row);
}

/** Unique operator segments in cue-sheet order (matches live/simulation timeline). */
export function buildBrowsableSegmentIdsFromCueSheet(rows: SimulationCueSheetRow[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  let lastKey: string | null = null;

  for (const row of rows) {
    const key = segmentKey(row);
    if (key === lastKey) {
      continue;
    }
    lastKey = key;

    const expandId = resolveExpandIdFromCueRow(row);
    if (!expandId || seen.has(expandId)) {
      continue;
    }
    seen.add(expandId);
    ids.push(expandId);
  }

  return ids;
}

function buildBrowsableSegmentIdsFromFlowSlots(flow: FlowDto): string[] {
  const ids: string[] = [FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID, FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID];

  for (const slot of flow.slots ?? []) {
    if (!isFlowControlSlotBrowsable(slot)) {
      continue;
    }
    ids.push(slot.id);
  }

  ids.push(FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID, FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID);
  return ids;
}

/** Ordered control-tab segment ids (pre-flow through after-flow). */
export function buildFlowControlBrowsableSegmentIds(
  flow: FlowDto | undefined,
  cueSheetRows: SimulationCueSheetRow[] = [],
): string[] {
  if (cueSheetRows.length > 0) {
    return buildBrowsableSegmentIdsFromCueSheet(cueSheetRows);
  }
  if (!flow) {
    return [];
  }
  return buildBrowsableSegmentIdsFromFlowSlots(flow);
}

export function resolveAdjacentBrowsableSegmentId(
  segmentIds: string[],
  currentId: string | null | undefined,
  delta: number,
): string | null {
  if (segmentIds.length === 0 || delta === 0) {
    return null;
  }

  const direction = delta > 0 ? 1 : -1;
  const currentIndex = currentId ? segmentIds.indexOf(currentId) : -1;

  if (currentIndex < 0) {
    return direction > 0 ? (segmentIds[0] ?? null) : (segmentIds[segmentIds.length - 1] ?? null);
  }

  const nextIndex = currentIndex + direction;
  if (nextIndex < 0 || nextIndex >= segmentIds.length) {
    return null;
  }
  return segmentIds[nextIndex] ?? null;
}

export function canStepBrowsableSegment(
  segmentIds: string[],
  currentId: string | null | undefined,
  delta: number,
): boolean {
  return resolveAdjacentBrowsableSegmentId(segmentIds, currentId, delta) != null;
}
